import { type MaybeRefOrGetter, onScopeDispose, toValue, watch } from 'vue';
import { useEventListener } from '@vueuse/core';

type EscapeLayer = {
  dismiss: () => void;
};

// Module-level: every overlay in the app shares one ordering, so the most recently opened
// layer is always the one Escape reaches, without any component knowing about the others.
const layers: EscapeLayer[] = [];

export const useEscapeLayer = (dismiss: () => void, isActive: MaybeRefOrGetter<boolean> = true) => {
  // There is nothing to dismiss on the server, and an immediate watcher still runs its
  // callback during an SSR render while scopes are never disposed — so registering there
  // would grow this module's stack for the life of the process. Same guard as useMediaQuery.
  if (typeof window === 'undefined') {
    return;
  }

  const layer: EscapeLayer = { dismiss };

  const setRegistered = (active: boolean) => {
    const index = layers.indexOf(layer);

    if (active && index === -1) {
      layers.push(layer);
    } else if (!active && index !== -1) {
      layers.splice(index, 1);
    }
  };

  watch(() => toValue(isActive), setRegistered, { immediate: true });
  onScopeDispose(() => setRegistered(false));

  // Target omitted on purpose: vueuse then resolves its SSR-safe `defaultWindow`, so a
  // consumer of this library rendered on the server does not blow up on a bare `window`.
  useEventListener('keydown', (event: KeyboardEvent) => {
    // `defaultPrevented` is how a control says it already used the key — an open HeadlessUI
    // listbox or combobox, a date picker menu. The layer must not act on that same press.
    if (event.key !== 'Escape' || event.defaultPrevented) {
      return;
    }

    if (layers[layers.length - 1] !== layer) {
      return;
    }

    event.preventDefault();
    layer.dismiss();
  });
};
